import {
  dispatch,
  FileUploadOutput,
  getExtraDetails,
  getWorkspaceId,
  updateExtraDetails,
  useAppSelector,
  useLazyGetChannelDetailsQuery,
  useLazyGetDispatchNoteParsedDataQuery,
} from '@zonofi/zono-money-store';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useState } from 'react';

export const SECTION_FIELD_KEYS: Record<string, string[]> = {
  transporter: [
    'transporterName',
    'vehicleNumber',
    'driverName',
    'driverPhone',
  ],
  destination: ['cityName', 'Pincode', 'address'],
  terms: ['termsOfDelivery'],
};

function getField(data: Record<string, any>, key: string): string {
  const lowerKey = key.toLowerCase();
  const found = Object.keys(data).find((k) => k.toLowerCase() === lowerKey);
  return found ? (data[found] ?? '') : '';
}

interface useDispatchNoteParseProps {
  selectedMedia: FileUploadOutput | undefined;
  imageParseState: {
    shouldParseImage: boolean;
    imageIdToParse?: number | undefined;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, string | string[]>>
  >;
  setOpenSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

export const useDispatchNoteParse = ({
  selectedMedia,
  imageParseState,
  setFormData,
  setOpenSections,
}: useDispatchNoteParseProps) => {
  const customerDetails = useAppSelector(getExtraDetails);
  const { customerId = '' } = customerDetails;
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [channelId, setChannelId] = useState<string>('');

  const [channelDetailsApi, channelDetailsResponse] =
    useLazyGetChannelDetailsQuery();
  const [fetchDispatchNoteParsedData, dispatchNoteParsedDataResponse] =
    useLazyGetDispatchNoteParsedDataQuery();

  useEffect(() => {
    const payload = {
      workspaceId: sellerWorkspaceId,
      sellerWorkspaceId: sellerWorkspaceId,
      customerId: customerId,
      channelType: 'C',
    };
    channelDetailsApi(payload);
  }, [customerId]);

  useEffect(() => {
    if (channelDetailsResponse.isSuccess && channelDetailsResponse.data?.id) {
      setChannelId(channelDetailsResponse.data?.id);
      dispatch(
        updateExtraDetails({
          customerId: channelDetailsResponse?.data?.customerId,
          customerName:
            channelDetailsResponse?.data?.customerName ??
            channelDetailsResponse?.data?.channelName,
        })
      );
    }
  }, [channelDetailsResponse]);

  useEffect(() => {
    if (
      !isEmpty(selectedMedia) &&
      channelId &&
      imageParseState?.shouldParseImage
    ) {
      setFormData({});
      setOpenSections({});

      const formData = new FormData();
      const activity = {
        type: 'upload',
        intent: 'invoice',
        channelId,
        inviteId: customerId,
        workspaceId: sellerWorkspaceId,
        text: 'Invoice',
      };
      formData.append('activity', JSON.stringify(activity));
      formData.append('file', selectedMedia!.file);
      fetchDispatchNoteParsedData({
        workspaceId: sellerWorkspaceId,
        body: formData,
      });
    }
  }, [selectedMedia, channelId, imageParseState?.shouldParseImage]);

  useEffect(() => {
    if (
      dispatchNoteParsedDataResponse.isSuccess &&
      dispatchNoteParsedDataResponse.data?.parsedText
    ) {
      const parsedData =
        dispatchNoteParsedDataResponse.data.parsedText?.response
          ?.extracted_data;

      // map parsed → formData
      const updatedFormData: Record<string, string> = {
        dispatchDocNo:
          getField(parsedData, 'dispatchDocNo') ||
          getField(parsedData, 'dispatch_number'),
        dispatchDate:
          getField(parsedData, 'deliveryNoteDate') ||
          getField(parsedData, 'date'),
        totalBox: getField(parsedData, 'totalBox'),
        totalWeight: getField(parsedData, 'totalWeight'),
        transporterName: getField(parsedData, 'transport_name'),
        vehicleNumber: getField(parsedData, 'vehicle_number'),
        driverName: getField(parsedData, 'driverName'),
        driverPhone: getField(parsedData, 'driverPhoneNumber'),
        pincode: getField(parsedData, 'pincode'),
        address: getField(parsedData, 'destination'),
        termsOfDelivery: getField(parsedData, 'termsOfDelivery'),
        cityName: getField(parsedData, 'city'),
      };
      setFormData((prev) => ({
        ...prev,
        ...updatedFormData,
      }));

      const newOpenSections: Record<string, boolean> = {};
      Object.entries(SECTION_FIELD_KEYS).forEach(([sectionKey, fieldKeys]) => {
        const hasAnyValue = fieldKeys.some((fieldKey) => {
          const value = updatedFormData[fieldKey];
          return !isNil(value) && !isEmpty(value);
        });

        newOpenSections[sectionKey] = hasAnyValue;
      });
      setOpenSections(newOpenSections);
    }
  }, [dispatchNoteParsedDataResponse]);

  return {
    isLoading:
      dispatchNoteParsedDataResponse?.isLoading ||
      dispatchNoteParsedDataResponse?.isFetching,
    channelId,
  };
};
